import { usePanelContentBg } from "@/theme/PanelContentBgContext";
import { useTheme } from "@/theme/ThemeProvider";
import { fieldFrame, mergeFieldStyle, type FieldLabelPosition, type FieldWidth } from "@/ui/components/fieldChrome";
import { focusHost, listPickerKeyAction } from "@/ui/components/listPickerKeyboard";
import { CaretDownIcon } from "phosphor-react-native";
import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from "react-native";

/** Max. Breite des Auswahl-Panels (Tablet/Web). */
const LIST_PICKER_PANEL_MAX_WIDTH = 400;

export type ListPickerOption = {
  value: string | null;
  label: string;
  /** Optional z. B. für Überbelegung (Anzeige in Warnfarbe) */
  warning?: boolean;
};

type Props = {
  label: string;
  options: ListPickerOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  required?: boolean;
  /** Platzhalter wenn required und nichts gewählt (z. B. "Ort wählen…") */
  placeholder?: string;
  containerStyle?: ViewStyle;
  /** Hinweis unter dem Feld, z. B. dass die Angabe optional ist. */
  hint?: string;
  /** Optionale Styling pro Option (z. B. Warnfarbe bei Überbelegung). Nutzt ggf. option.warning. */
  optionStyle?: (option: ListPickerOption) => { color?: string } | undefined;
  /** Steigt, wenn der Auslöser den Fokus erhalten soll. */
  focusRequest?: number;
  labelPosition?: FieldLabelPosition;
  dense?: boolean;
  width?: FieldWidth;
};

/**
 * Einheitliches Dropdown im Stil eines FloatingLabelInput:
 * einzeiliges Feld mit Label, Tipp öffnet Modal mit Liste.
 * Wird für Pflegeort, Bereich (Unit) und andere Listen-Auswahlen genutzt.
 */
export function FloatingLabelListPicker({
  label,
  options,
  value,
  onChange,
  required = false,
  placeholder,
  containerStyle,
  hint,
  optionStyle,
  focusRequest = 0,
  labelPosition = "inset",
  dense = false,
  width,
}: Props) {
  const { theme } = useTheme();
  const frame = fieldFrame(theme, { labelPosition, dense, width });
  const panelContentBg = usePanelContentBg();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<View>(null);
  const optionRefs = useRef<(View | null)[]>([]);
  const optionsRef = useRef(options);
  const onChangeRef = useRef(onChange);
  const activeRef = useRef(0);
  optionsRef.current = options;
  onChangeRef.current = onChange;
  activeRef.current = activeIndex;

  const selected = options.find(
    (o) => o.value === value || (o.value != null && String(o.value) === String(value))
  );
  const displayText = selected
    ? selected.label
    : required
      ? placeholder ?? "Bitte wählen…"
      : placeholder ?? "Keine Auswahl";
  const selectedIndex = selected ? options.indexOf(selected) : 0;

  const borderColor = theme.color.inputBorder;
  const bgColor = panelContentBg ?? "transparent";

  const restoreFocus = () => {
    requestAnimationFrame(() => focusHost(triggerRef.current));
  };

  const closePicker = () => {
    setOpen(false);
    restoreFocus();
  };

  const chooseIndex = (index: number) => {
    const opt = optionsRef.current[index];
    if (!opt) {
      closePicker();
      return;
    }
    onChangeRef.current(opt.value);
    setOpen(false);
    restoreFocus();
  };

  const openPicker = () => {
    const idx = selectedIndex >= 0 ? selectedIndex : 0;
    activeRef.current = idx;
    setActiveIndex(idx);
    setOpen(true);
  };

  const chooseRef = useRef(chooseIndex);
  const closeRef = useRef(closePicker);
  const keyboardChoseRef = useRef(false);
  chooseRef.current = chooseIndex;
  closeRef.current = closePicker;

  const focusOption = (index: number) => {
    const next = options.length === 0 ? 0 : Math.min(Math.max(index, 0), options.length - 1);
    activeRef.current = next;
    setActiveIndex(next);
  };

  useEffect(() => {
    if (!open) return;
    focusHost(optionRefs.current[activeIndex]);
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open || Platform.OS !== "web" || typeof window === "undefined") return;
    if (typeof window.addEventListener !== "function") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const action = listPickerKeyAction(
        event.key,
        activeRef.current,
        optionsRef.current.length
      );
      if (action.type === "ignore") return;
      event.preventDefault();
      if (action.type === "move") {
        activeRef.current = action.index;
        setActiveIndex(action.index);
        return;
      }
      if (action.type === "select") {
        // Pressable auf Web löst Enter danach noch einmal als Druck aus.
        keyboardChoseRef.current = true;
        chooseRef.current(action.index);
        return;
      }
      closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!focusRequest) return;
    focusHost(triggerRef.current);
  }, [focusRequest]);

  const trigger = (
    <View style={[{ position: "relative", opacity: 1 }, frame.above || frame.widthStyle ? undefined : containerStyle]}>
      <Pressable
        ref={triggerRef}
        accessibilityRole="button"
        accessibilityLabel={`${label}, ${displayText}`}
        {...(required ? { "aria-required": true as const } : {})}
        onPress={openPicker}
        style={[
          {
            position: "relative",
            borderWidth: 1,
            borderColor,
            backgroundColor: bgColor,
            borderRadius: frame.radius,
            paddingHorizontal: frame.paddingH,
            paddingTop: frame.paddingTop,
            paddingBottom: frame.paddingV,
            paddingRight: 40,
            minHeight: frame.minHeight,
            flexDirection: "row",
            alignItems: "center",
            opacity: 1,
          },
        ]}
      >
        {frame.above ? null : (
          <Text
            style={{
              position: "absolute",
              left: 12,
              top: -8,
              fontSize: frame.labelSize,
              color: theme.color.inputLabelFloatingColor,
              backgroundColor: panelContentBg ?? theme.color.inputLabelBg,
              paddingHorizontal: 6,
              borderRadius: 6,
            }}
          >
            {label}
            {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
          </Text>
        )}
        <Text
          style={{
            flex: 1,
            paddingTop: 4,
            color: selected ? theme.color.text : theme.color.inputPlaceholder,
            fontSize: 14,
            fontWeight: "600",
          }}
          numberOfLines={1}
        >
          {displayText}
        </Text>
        <View style={{ position: "absolute", right: 12, top: 14 }}>
          <CaretDownIcon size={18} color={theme.color.muted} />
        </View>
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={closePicker}
      >
        <View style={styles.modalFullScreen}>
          <Pressable
            style={[StyleSheet.absoluteFill, styles.overlay]}
            onPress={closePicker}
            accessibilityRole="button"
            accessibilityLabel="Auswahl schließen"
          />
          <View style={styles.centeredContainer} pointerEvents="box-none">
            <View
              style={[
                styles.panel,
                {
                  borderWidth: 1,
                  borderColor: theme.color.border,
                  borderRadius: theme.radius?.panel ?? theme.radius?.lg ?? 14,
                  overflow: "hidden",
                  backgroundColor: theme.color.sectionCardBodyBg,
                },
              ]}
            >
              <View
                style={[
                  styles.panelHead,
                  {
                    borderBottomWidth: 1,
                    borderBottomColor: theme.color.border,
                    backgroundColor: theme.color.sectionCardBodyBg,
                  },
                ]}
              >
                <Text
                  style={[styles.panelHeadTitle, { color: theme.color.text }]}
                  numberOfLines={1}
                >
                  {label}
                </Text>
              </View>
              <ScrollView
                style={[styles.panelBody, { backgroundColor: theme.color.sectionCardBodyBg }]}
                keyboardShouldPersistTaps="handled"
              >
                {options.map((opt, index) => {
                  const isSelected =
                    (opt.value == null && value == null) ||
                    (opt.value != null &&
                      (opt.value === value || String(opt.value) === String(value)));
                  const isActive = index === activeIndex;
                  const extraStyle =
                    optionStyle?.(opt) ?? (opt.warning ? { color: theme.color.warning } : undefined);
                  return (
                    <Pressable
                      key={opt.value ?? "__none__"}
                      ref={(node) => {
                        optionRefs.current[index] = node;
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={opt.label}
                      accessibilityState={{ selected: isSelected }}
                      onFocus={() => focusOption(index)}
                      onPress={() => {
                        if (keyboardChoseRef.current) {
                          keyboardChoseRef.current = false;
                          return;
                        }
                        chooseIndex(index);
                      }}
                      style={[
                        styles.optionRow,
                        {
                          borderBottomColor: isActive
                            ? theme.color.accent
                            : theme.color.border,
                          borderBottomWidth: isActive ? 2 : 1,
                          backgroundColor: isActive
                            ? theme.color.accentBg
                            : isSelected
                              ? theme.color.infoBg
                              : "transparent",
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.optionText,
                          {
                            color: extraStyle?.color ?? theme.color.text,
                            fontWeight: isSelected ? "700" : "600",
                          },
                        ]}
                      >
                        {opt.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
          </View>
        </View>
      </Modal>
      {hint ? (
        <Text
          style={{
            marginTop: theme.space.xs,
            color: theme.color.textSecondary,
            fontSize: theme.fontSize.small,
          }}
        >
          {hint}
        </Text>
      ) : null}
    </View>
  );

  if (!frame.above && !frame.widthStyle) return trigger;

  return (
    <View style={mergeFieldStyle(frame, containerStyle)}>
      {frame.above ? (
        <View style={{ gap: theme.space.dense }}>
          <Text
            style={{
              color: theme.color.textSecondary,
              fontSize: frame.labelSize,
              fontWeight: "600",
            }}
          >
            {label}
            {required ? <Text style={{ color: theme.color.dangerText }}> *</Text> : null}
          </Text>
          {trigger}
        </View>
      ) : (
        trigger
      )}
    </View>
  );
}

const HEAD_MIN_HEIGHT = 44;

const styles = StyleSheet.create({
  modalFullScreen: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    minWidth: "100%",
    minHeight: "100%",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  centeredContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  panel: {
    width: "100%",
    maxWidth: LIST_PICKER_PANEL_MAX_WIDTH,
    maxHeight: "70%",
  },
  panelHead: {
    minHeight: HEAD_MIN_HEIGHT,
    paddingVertical: 10,
    paddingHorizontal: 12,
    justifyContent: "center",
  },
  panelHeadTitle: {
    fontSize: 15,
    fontWeight: "800",
    letterSpacing: 0.15,
  },
  panelBody: {
    maxHeight: 320,
  },
  optionRow: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  optionText: {
    fontSize: 14,
  },
});
